import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ServerTable } from 'shared/ui/ServerTable';
import { ReactComponent as BoxEditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { useRole, UserPermissions } from 'entities/User';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { ColumnsType } from 'antd/es/table';
import { Toggle } from 'shared/ui/Toggle';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useGetAllWarehousesQuery } from 'entities/Warehouse';
import { RentOptionFormDrawer, rentOptionFormDrawerActions, DrawerMode } from 'widgets/RentOptionFormDrawer';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';
import {
  RentOptionDocument,
  useGetPaginatedRentOptionListingQuery,
  useUpdateRentOptionMutation,
  useDeleteRentOptionMutation,
} from 'entities/RentOption';

export const RentOptionsTable: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const dispatch = useAppDispatch();

  const { openModal, closeModal } = useGlobalConfirmModal();

  const isForbiddenCreateRentOption = useCheckPermission(UserPermissions.CREATE_UNITS);
  const isForbiddenDeleteRentOption = useCheckPermission(UserPermissions.DELETE_UNITS);
  const isForbiddenEditRentOption = useCheckPermission(UserPermissions.EDIT_UNITS);

  const { data: warehouses } = useGetAllWarehousesQuery();
  const [deleteRentOption] = useDeleteRentOptionMutation();
  const [updateRentOption] = useUpdateRentOptionMutation();

  const createRentOption = useCallback((): void => {
    dispatch(rentOptionFormDrawerActions.setOpenRentOptionForm({ mode: DrawerMode.RENT_OPTION_CREATE }));
  }, [dispatch]);

  const editRentOption = useCallback(
    (rentOption: RentOptionDocument): void => {
      dispatch(rentOptionFormDrawerActions.setOpenRentOptionForm({ mode: DrawerMode.RENT_OPTION_EDIT, rentOption }));
    },
    [dispatch],
  );

  const toggleRentOptionActivation = useCallback(
    async (rentOption: RentOptionDocument) => {
      try {
        await updateRentOption({ rentOptionId: rentOption.rentOptionId, isActive: !rentOption.isActive }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Rent option status has been successfully changed'));
      } catch (error: CustomAny) {
        console.log(error);
        showNotification('error', t('Error', { ns: 'common' }), 'Error when changing rent option status');
      }
    },
    [t, updateRentOption],
  );

  const handleDeleteRentOption = useCallback(
    async (selectedRentOption: RentOptionDocument): Promise<void> => {
      try {
        await deleteRentOption(selectedRentOption.rentOptionId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Rent option deleted successfully'));
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting rent option'));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [closeModal, deleteRentOption, t],
  );

  const openRentOptionDeletionConfirmModal = useCallback(
    (selectedRentOption: RentOptionDocument): void => {
      openModal({
        title: t('Delete rent option'),
        description: t('Are you sure you want to delete this rent option?'),
        onCancel: closeModal,
        onOk: async () => {
          await handleDeleteRentOption(selectedRentOption);
        },
        isConfirmDisabled: isForbiddenDeleteRentOption,
      });
    },
    [closeModal, handleDeleteRentOption, isForbiddenDeleteRentOption, openModal, t],
  );

  const rowActions: Array<ServerTableRowActions<RentOptionDocument>> = useMemo(
    () => [
      {
        name: 'editRentOption',
        icon: <BoxEditIcon />,
        theme: 'clear',
        description: t('Edit rent option'),
        onClick: editRentOption,
      },
      {
        name: 'deleteRentOption',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete discount'),
        onClick: openRentOptionDeletionConfirmModal,
      },
    ],
    [editRentOption, openRentOptionDeletionConfirmModal, t],
  );

  const tableActions: Array<ServerTableActions<RentOptionDocument>> = useMemo(
    () => [
      {
        name: t('Add Rent option'),
        icon: <PlusIcon />,
        iconPosition: 'prev',
        onClick: createRentOption,
        disabled: isForbiddenCreateRentOption,
      },
    ],
    [t, createRentOption, isForbiddenCreateRentOption],
  );

  const columns = useMemo<ColumnsType<RentOptionDocument>>(
    () => [
      {
        title: t('Label'),
        key: 'label',
        render: (_, record) => {
          return <div>{getLocalizedString(record.label)}</div>;
        },
      },
      {
        title: t('Warehouse'),
        key: 'warehouse',
        render: (_, record) => {
          const assignedWarehouses = warehouses?.filter((warehouse) => record.warehouseIds?.includes(warehouse.warehouseId));

          if (!assignedWarehouses?.length) {
            return <div>{t('All')}</div>;
          }

          return (
            <div className="text-primary font-normal">
              {assignedWarehouses?.map((warehouse) => {
                return <div key={warehouse.warehouseId}>{warehouse.name}</div>;
              })}
            </div>
          );
        },
      },
      {
        title: t('Unit type'),
        key: 'unitTypeSetting',
        render: (_, record) => {
          return (
            <div className="text-primary font-normal">
              <div>{t(record.unitTypeSetting)}</div>
            </div>
          );
        },
      },
      {
        title: t('Client loyalty'),
        key: 'loyaltyUserSetting',
        render: (_, record) => {
          return <div className="text-primary font-normal">{t(record.loyaltyUserSetting)}</div>;
        },
      },
      {
        title: t('Options'),
        key: 'options',
        render: (_, record) => {
          return (
            <div>
              <div className="text-primary font-normal flex gap-1">
                {t('Account type')}: <div>{t(record.accountTypeSetting)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Min contract duration')}: <div>{record.minContractDuration}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Perid type')}: <div>{t(record.invoiceFrequencyType)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Payment frequency')}: <div>{t(record.paymentFrequencySetting)}</div>
              </div>
              <div className="text-primary font-normal flex gap-1">
                {t('Order')}: <div>{record.order}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('Discount key'),
        key: 'discountKeys',
        render: (_, record) => {
          return <div className="text-primary">{record.discountKeys}</div>;
        },
      },
      {
        title: t('Status'),
        key: 'isActive',
        render: (_, record) => (
          <div>
            <Toggle
              checked={record.isActive}
              isDisabled={isForbiddenEditRentOption}
              onChange={async () => {
                await toggleRentOptionActivation(record);
              }}
            />
            <div className="text-xs text-primaryLight mt-2">{t(record.isActive ? 'Enabled' : 'Disabled')}</div>
          </div>
        ),
      },
      {
        title: t('Description'),
        key: 'description',

        render: (_, record) => {
          return <div className="text-primaryLight font-normal">{getLocalizedString(record.description)}</div>;
        },
      },
    ],
    [t, warehouses, isForbiddenEditRentOption, toggleRentOptionActivation],
  );

  return (
    <div className="pt-4">
      <div className="font-semibold text-3xl mb-7 font-tektur">{t('Rent Options List')}</div>
      <ServerTable
        rowActions={rowActions}
        columns={columns}
        fetchData={useGetPaginatedRentOptionListingQuery}
        rowKey="rentOptionId"
        search
        tableActions={tableActions}
        tableActionsPosition="prev"
      />
      <RentOptionFormDrawer />
    </div>
  );
});
